import { cn } from "@/utils/cn"
import { PortableText } from "@portabletext/react"
import { SanityBlock } from "sanity-codegen"

export type RichTextValue = SanityBlock[]

interface RichTextFieldProps {
  value: SanityBlock[]
  className?: string
  multiline?: boolean
  boxClassName?: string
  billboardClassName?: string
}

export function RichText({
  value,
  billboardClassName,
  className,
  boxClassName,
}: RichTextFieldProps) {
  return (
    <div className={className}>
      <PortableText
        value={value}
        components={{
          block: {
            h1: (p) => (
              <h1 className="mb-8 text-[5rem] font-bold last:mb-0 md:text-8xl md:break-normal xl:text-9xl">
                {p.children}
              </h1>
            ),
            h2: (p) => (
              <h2 className="sm:mb-4 md:mb-8 text-3xl font-bold last:mb-0 md:text-5xl xl:text-6xl">
                {p.children}
              </h2>
            ),
            h3: (p) => <h3 className="text-2xl font-bold">{p.children}</h3>,
            h4: (p) => (
              <h4 className="mb-8 text-xl font-medium last:mb-0">
                {p.children}
              </h4>
            ),
            normal: ({ children }) => (
              <div className="mb-8 text-base font-medium last:mb-0">
                {children}
              </div>
            ),
            large: ({ children }) => (
              <p className="mb-8 text-lg font-light last:mb-0 xl:text-xlg">
                {children}
              </p>
            ),
            blockquote: ({ children }) => (
              <blockquote className="mb-8 border-l-4 pl-4 text-2xl font-bold italic last:mb-0">
                {children}
              </blockquote>
            ),
          },
          marks: {
            box: ({ children }: { children: React.ReactNode }) => (
              <span
                className={cn(
                  "relative mx-1 my-1 inline-block border-4 border-current",
                  boxClassName
                )}
              >
                <div className="absolute top-0 left-0 h-2 w-2 -translate-x-full -translate-y-full border-2 border-current bg-neutral-light" />
                <div className="absolute top-0 right-0 h-2 w-2 translate-x-full -translate-y-full border-2 border-current bg-neutral-light" />
                <div className="absolute right-0 bottom-0 h-2 w-2 translate-x-full translate-y-full border-2 border-current bg-neutral-light" />
                <div className="absolute bottom-0 left-0 h-2 w-2 -translate-x-full translate-y-full border-2 border-current bg-neutral-light" />
                {children}
              </span>
            ),
            viola: (p) => <span className="text-viola-500">{p.children}</span>,
            lightblue: (p) => (
              <span className="text-primary-500">{p.children}</span>
            ),
            blue: (p) => <span className="text-primary-800">{p.children}</span>,
            green: (p) => <span className="text-green-500">{p.children}</span>,
            "crooked-billboard": (p) => (
              <Billboard
                containerClassName="inline-block -rotate-5"
                className={billboardClassName}
                text={p.text}
              />
            ),
            billboard: ({ text }) => (
              <Billboard className={billboardClassName} text={text} />
            ),
            pen: (p) => (
              <span className="relative">
                <svg
                  viewBox="0 0 244 39"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute -bottom-5 left-0 h-auto w-full"
                >
                  <path d="M23.2729 19.015C22.361 15.1851 37.827 14.0651 39.8717 13.8536C88.9335 8.77827 138.623 7.50318 187.857 5.05873C204.846 4.21527 255.7 0.867784 238.851 3.20065C228.047 4.69655 216.956 5.03863 206.108 5.96713C181.987 8.03148 157.942 10.5846 133.932 13.6885C94.2163 18.8226 54.3776 24.5922 15.1799 32.8886C11.8452 33.5944 8.51728 34.3453 5.22886 35.2422C3.96844 35.5859 1.7792 35.3179 1.55399 36.6048C1.35874 37.7205 3.80714 36.9018 4.93982 36.8938C9.59944 36.861 24.3435 35.7963 27.1542 35.6138C86.9301 31.7322 148.93 26.208 208.337 36.1093" />
                </svg>
                {p.children}
              </span>
            ),
            link: ({
              value,
              children,
            }: {
              value?: { href?: string }
              children?: React.ReactNode
            }) => {
              return (
                <a href={value?.href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              )
            },
            highlight: ({ children }) => {
              return (
                <span className="inline-block bg-viola-500 p-0.5 font-family-gthaptik font-light text-neutral-light">
                  {children}
                </span>
              )
            },
          },
        }}
      />
    </div>
  )
}

const billboardPaths = [
  "M0 0L5.52381 1.13295L9.66667 0.566474L15.881 1.13295L37.9762 0L54.5476 0.566474L58 0L57.3095 66.8439L58 97.4335L54.5476 98L13.119 95.7341L11.0476 95.1676L9.66667 96.8671L8.28571 96.3006L0 97.4335L1.38095 48.7168L0 0Z",
  "M0.978795 2.68594L3.79476 2.0293L10.2667 3.94481L18.027 2.54622L47.7796 0.265957L60.5712 0.296888L62.0336 1.32575L67.6655 0.0124774L66.9083 34.2986L70.2772 65.1569L71.4305 93.9259L60.0574 94.3819L36.6772 94.5294L17.3584 93.9061L15.8363 95.6078L14.2267 95.1254L7.00146 97.7342L6.4937 69.3516L5.25292 38.4016L0.978795 2.68594Z",
  "M0.0937604 2.05172L3.46276 1.97349L6.24598 5.7018L10.9123 3.42606L19.6465 2.13955L27.7447 2.49336L41.8693 1.08123L53.3616 2.44037L66.7999 0.502342L70.1689 0.42456L69.6063 63.2948L72.3443 94.1108L66.318 95.8822L58.2324 96.0699L52.7915 94.0199L21.1231 94.7642L15.5662 93.2493L13.9357 94.9221L8.28277 93.4184L2.26917 95.738L3.2543 22.0294L0.0937604 2.05172Z",
  "M3.98181 0.0489706L10.2078 1.39716L14.9348 1.05675L41.471 3.07721L48.8556 2.0636L51.5737 4.7777L58.2611 1.42523L60.9726 3.24308L65.6963 4.89785L69.8379 2.85192L65.9633 31.2718L66.3387 66.6121L65.8802 95.8391L61.9372 96.2129L16.8819 93.285L13.5189 93.1509L10.1468 92.1514L3.446 94.2472L0.0241144 93.0362L4.59962 58.1979L3.98181 0.0489706Z",
]

function Billboard({
  containerClassName,
  text,
  className,
}: {
  containerClassName?: string
  text: string
  className?: string
}) {
  const letters = text.toString().split("")
  return (
    <span className={containerClassName}>
      {letters.map((letter, i) => (
        <span key={i} className="relative">
          {letter !== " " && (
            <svg
              className={cn(
                "pointer-none absolute inset-0 h-full w-full text-viola-500",
                className
              )}
              width="58"
              height="98"
              viewBox="0 0 58 98"
              fill="currentColor"
            >
              <path d={billboardPaths[i % billboardPaths.length]} />
            </svg>
          )}
          <span
            className={cn(
              "relative text-current",
              i % 2 === 0 ? "font-bold" : "font-family-gtpressura font-normal"
            )}
          >
            {letter}
          </span>
        </span>
      ))}
    </span>
  )
}
